import { Controller } from "@hotwired/stimulus"

// Handles cursor-based infinite scroll on the discoveries feed.
// Watches a sentinel element at the bottom of the list; when it becomes
// visible, fetches the next page as a Turbo Stream and appends the results.
// The sentinel carries its own cursor via data-next-cursor.
export default class extends Controller {
  static targets = ["sentinel"]

  connect() {
    this._observer = new IntersectionObserver(this._onIntersect.bind(this), {
      rootMargin: "200px"
    })
    if (this.hasSentinelTarget) {
      this._observer.observe(this.sentinelTarget)
    }
  }

  disconnect() {
    this._observer?.disconnect()
  }

  sentinelTargetConnected(el) {
    this._observer?.observe(el)
  }

  sentinelTargetDisconnected(el) {
    this._observer?.unobserve(el)
  }

  _onIntersect(entries) {
    const visible = entries.find(e => e.isIntersecting)
    if (!visible || this._loading) return

    // Prefer a fully-formed next URL (group timeline); fall back to the feed's
    // cursor-only form for back-compat.
    const url = visible.target.dataset.nextUrl ||
      (visible.target.dataset.nextCursor
        ? `/discoveries?cursor=${encodeURIComponent(visible.target.dataset.nextCursor)}`
        : null)
    if (!url) return

    this._loading = true
    this._fetch(url)
  }

  async _fetch(url) {
    this._showSpinner()
    try {
      const response = await fetch(url, {
        headers: {
          "Accept":           "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })
      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } finally {
      this._loading = false
      this._hideSpinner()
    }
  }

  _showSpinner() {
    const el = document.createElement("div")
    el.id = "infinite-scroll-spinner"
    el.style.cssText = "text-align:center;padding:20px;color:#9ca3af;font-size:13px;font-family:-apple-system, BlinkMacSystemFont, sans-serif;"
    el.textContent = "Loading more…"
    this.element.appendChild(el)
  }

  _hideSpinner() {
    document.getElementById("infinite-scroll-spinner")?.remove()
  }
}
